import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TablePagination,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { deleteDoc, doc } from 'firebase/firestore';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { type KnowledgeDocument, useKnowledgeDocuments } from '../../hooks/useKnowledgeDocuments';
import { firestore } from '../../services/firebase';
import storageService from '../../services/storageService';
import {
  ARCHETYPE_LABELS,
  type CollectionArchetype,
  DOMAIN_ARCHETYPES,
} from '../../types/knowledge';
import type { Column } from '../common/UI/StandardTable';
import StandardTable from '../common/UI/StandardTable';

interface Props {
  collectionType: CollectionArchetype;
  isSyncing?: boolean;
}

export const KnowledgeDocumentsTable: React.FC<Props> = ({ collectionType, isSyncing }) => {
  const { documents, loading, error } = useKnowledgeDocuments(collectionType);
  const { account } = useAccount();
  const { showSnackbar } = useSnackbar();

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState<keyof KnowledgeDocument>('updatedAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [moveToArchetype, setMoveToArchetype] = useState<CollectionArchetype | ''>('');
  const [isMoving, setIsMoving] = useState(false);

  const handleDelete = async (row: KnowledgeDocument) => {
    if (!account?.clientId) {
      return;
    }
    setIsDeleting(row.docId);
    try {
      if (row.storageType === 'gcs' && row.sourceUrl) {
        try {
          await storageService.deleteFile(row.sourceUrl);
        } catch (_e) {
          // ignore
        }
      }
      await deleteDoc(
        doc(firestore, 'clients', account.clientId, 'knowledge_documents', row.docId)
      );
      showSnackbar('Document deleted successfully', 'success');
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(row.docId);
        return next;
      });
    } catch (_e) {
      showSnackbar('Failed to delete document', 'error');
    } finally {
      setIsDeleting(null);
    }
  };

  const handleBatchDelete = async () => {
    if (!account?.clientId || selectedIds.size === 0) {
      return;
    }
    setIsBatchDeleting(true);
    let successCount = 0;
    try {
      const rowsToDelete = documents.filter((d) => selectedIds.has(d.docId));
      for (const row of rowsToDelete) {
        if (row.storageType === 'gcs' && row.sourceUrl) {
          try {
            await storageService.deleteFile(row.sourceUrl);
          } catch (_e) {
            // Ignore missing GCS files
          }
        }
        await deleteDoc(
          doc(firestore, 'clients', account.clientId, 'knowledge_documents', row.docId)
        );
        successCount++;
      }
      showSnackbar(`Successfully deleted ${successCount} documents`, 'success');
      setSelectedIds(new Set());
    } catch (_e) {
      showSnackbar('Failed to delete some documents', 'error');
    } finally {
      setIsBatchDeleting(false);
    }
  };

  const handleBatchMove = async () => {
    if (!account?.clientId || selectedIds.size === 0 || !moveToArchetype) {
      return;
    }
    setIsMoving(true);
    let successCount = 0;
    try {
      const { moveKnowledgeDocument } = await import('../../services/api');
      const rowsToMove = documents.filter((d) => selectedIds.has(d.docId));
      for (const row of rowsToMove) {
        await moveKnowledgeDocument({
          docId: row.docId,
          sourceCollectionType: row.collectionType,
          targetCollectionType: moveToArchetype,
          userId: account.uid,
          clientId: account.clientId,
        });
        successCount++;
      }
      showSnackbar(
        `Successfully moved ${successCount} documents to ${ARCHETYPE_LABELS[moveToArchetype as CollectionArchetype]}`,
        'success'
      );
      setSelectedIds(new Set());
      setIsMoveDialogOpen(false);
      setMoveToArchetype('');
    } catch (_e) {
      showSnackbar('Failed to move some documents', 'error');
    } finally {
      setIsMoving(false);
    }
  };

  const handleSort = (property: keyof KnowledgeDocument) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const processedDocuments = useMemo(() => {
    let result = [...documents];

    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter((doc) => doc.fileName.toLowerCase().includes(lowerQuery));
    }

    result.sort((a, b) => {
      let aValue = a[orderBy] || '';
      let bValue = b[orderBy] || '';

      if (orderBy === 'updatedAt') {
        aValue = new Date(a.updatedAt).getTime() as unknown as string;
        bValue = new Date(b.updatedAt).getTime() as unknown as string;
      }

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });

    const start = page * rowsPerPage;
    return {
      paginated: result.slice(start, start + rowsPerPage),
      totalCount: result.length,
    };
  }, [documents, searchQuery, orderBy, order, page, rowsPerPage]);

  if (error) {
    return (
      <Typography color="error" variant="body2" sx={{ p: 2 }}>
        Error loading documents: {error}
      </Typography>
    );
  }

  const columns: Column<KnowledgeDocument>[] = [
    {
      id: 'select',
      width: '50px',
      label: (
        <Checkbox
          size="small"
          checked={
            processedDocuments.paginated.length > 0 &&
            selectedIds.size === processedDocuments.paginated.length
          }
          indeterminate={
            selectedIds.size > 0 && selectedIds.size < processedDocuments.paginated.length
          }
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedIds(new Set(processedDocuments.paginated.map((d) => d.docId)));
            } else {
              setSelectedIds(new Set());
            }
          }}
        />
      ),
      render: (row) => (
        <Checkbox
          size="small"
          checked={selectedIds.has(row.docId)}
          onChange={(e) => {
            const next = new Set(selectedIds);
            if (e.target.checked) {
              next.add(row.docId);
            } else {
              next.delete(row.docId);
            }
            setSelectedIds(next);
          }}
        />
      ),
    },
    {
      id: 'fileName',
      label: (
        <TableSortLabel
          active={orderBy === 'fileName'}
          direction={orderBy === 'fileName' ? order : 'asc'}
          onClick={() => handleSort('fileName')}
        >
          File Name
        </TableSortLabel>
      ),
      render: (row) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '300px' }}>
          <Tooltip title={`File: ${row.fileName}\nSource: ${row.sourceUrl}`} placement="top-start">
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{
                cursor: 'help',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {row.fileName}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      id: 'storageType',
      label: (
        <TableSortLabel
          active={orderBy === 'storageType'}
          direction={orderBy === 'storageType' ? order : 'asc'}
          onClick={() => handleSort('storageType')}
        >
          Origin
        </TableSortLabel>
      ),
      render: (row) => (
        <Chip
          label={row.storageType === 'gcs' ? 'Cloud Storage' : 'SharePoint'}
          size="small"
          color={row.storageType === 'gcs' ? 'primary' : 'info'}
          variant="outlined"
        />
      ),
    },
    {
      id: 'syncStatus',
      label: (
        <TableSortLabel
          active={orderBy === 'syncStatus'}
          direction={orderBy === 'syncStatus' ? order : 'asc'}
          onClick={() => handleSort('syncStatus')}
        >
          Status
        </TableSortLabel>
      ),
      render: (row) => {
        let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' =
          'default';
        let label: string = row.syncStatus;
        if (row.syncStatus === 'ready') {
          color = 'success';
          label = 'Indexed';
        } else if (row.syncStatus === 'processing') {
          color = 'warning';
          label = 'Processing...';
        } else if (row.syncStatus === 'error') {
          color = 'error';
          label = 'Failed';
        } else if (row.syncStatus === 'pending') {
          color = 'default';
          label = 'Queued';
        }

        return <Chip label={label} size="small" color={color} variant="outlined" />;
      },
    },
    {
      id: 'updatedAt',
      label: (
        <TableSortLabel
          active={orderBy === 'updatedAt'}
          direction={orderBy === 'updatedAt' ? order : 'asc'}
          onClick={() => handleSort('updatedAt')}
        >
          Last Updated
        </TableSortLabel>
      ),
      render: (row) => (
        <Typography variant="body2">{new Date(row.updatedAt).toLocaleDateString()}</Typography>
      ),
    },
    {
      id: 'actions' as keyof KnowledgeDocument, // Explicit cast since actions is not in KnowledgeDocument interface
      label: 'Actions',
      render: (row) => (
        <Tooltip title="Delete Document">
          <span>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(row)}
              disabled={isDeleting === row.docId}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      {selectedIds.size > 0 && (
        <Box sx={{ p: 2, bgcolor: 'primary.main', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle2" sx={{ color: 'primary.contrastText', flexGrow: 1 }}>
            {selectedIds.size} selected
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<DriveFileMoveIcon />}
            onClick={() => setIsMoveDialogOpen(true)}
            sx={{
              bgcolor: 'bidlogiq.brick',
              color: '#ffffff',
              '&:hover': {
                bgcolor: 'bidlogiq.brick',
                filter: 'brightness(0.9)',
              },
            }}
          >
            Move
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleBatchDelete}
            disabled={isBatchDeleting}
            sx={{
              bgcolor: 'error.main',
              color: '#ffffff',
              '&:hover': {
                bgcolor: 'error.dark',
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(211, 47, 47, 0.4)', // Dimmed red for disabled state
                color: 'rgba(255, 255, 255, 0.8)', // Keep text readable
              },
            }}
          >
            {isBatchDeleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </Box>
      )}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          placeholder="Search files..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <StandardTable
        columns={columns}
        data={processedDocuments.paginated}
        keyExtractor={(row) => row.docId}
        isLoading={loading || !!isSyncing}
        emptyStateMessage={
          isSyncing
            ? 'Syncing documents... This may take a few moments depending on the number of files.'
            : 'No documents found for this collection. Upload or sync files to get started.'
        }
      />
      <TablePagination
        component="div"
        count={processedDocuments.totalCount}
        page={page}
        onPageChange={(_e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number.parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100]}
      />

      <Dialog
        open={isMoveDialogOpen}
        onClose={() => setIsMoveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Move Documents</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Destination Collection</InputLabel>
            <Select
              value={moveToArchetype}
              label="Destination Collection"
              onChange={(e) => setMoveToArchetype(e.target.value as CollectionArchetype)}
            >
              {Object.keys(DOMAIN_ARCHETYPES).flatMap((domain) => [
                <ListSubheader key={`header-${domain}`}>
                  {domain.toUpperCase()} DOMAIN
                </ListSubheader>,
                ...DOMAIN_ARCHETYPES[domain as keyof typeof DOMAIN_ARCHETYPES].map((archetype) => (
                  <MenuItem key={archetype} value={archetype} sx={{ pl: 4 }}>
                    {ARCHETYPE_LABELS[archetype]}
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsMoveDialogOpen(false)} disabled={isMoving}>
            Cancel
          </Button>
          <Button
            onClick={handleBatchMove}
            variant="contained"
            disabled={!moveToArchetype || isMoving}
          >
            {isMoving ? <CircularProgress size={24} /> : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
